import { canReadProperties } from '@/api/core/authorization';
import { NATIVE_PROPERTY_PAGE_SIZE } from '@/api/core/native-property-read';
import { isApiError } from '@/api/http/errors';
import { ShellHeader } from '@/app/shell-header';
import { useOperatorSession } from '@/auth/runtime';
import { ListNote } from '@/components/list-note';
import { LoadFailure } from '@/components/load-failure';
import { LoadingAnnouncement } from '@/components/loading-announcement';
import { useUrlState } from '@/lib/url-state';
import { Counter, ListPage, PageHeader } from '@mgrvtechgroup/admin-ds';
import { Building2Icon, SearchXIcon, Trash2Icon } from 'lucide-react';
import { useState } from 'react';
import { useLocation, useNavigate } from 'react-router';
import { DeletedNativePropertyTable } from './deleted-native-property-table';
import { NativePropertyCreate } from './native-property-create';
import { NativePropertyListPager } from './native-property-list-pager';
import { useDeletedNativeProperties, useNativePropertyList } from './native-property-list-queries';
import {
  deletedPropertyListNote,
  filterDeletedProperties,
  filterNativeProperties,
  isNativePropertyListFiltered,
  nativePropertyListEmpty,
  nativePropertyListNote,
  parseNativePropertyListQuery,
  patchFromApplicationSegment,
  type NativePropertyListView,
} from './native-property-list-query';
import { NativePropertyListToolbar } from './native-property-list-toolbar';
import { NativePropertyTable } from './native-property-table';
import { nativePropertyListReturnTo, nativePropertyNameHint } from './native-property-url';
import type { PropertyRuntime } from './property-queries';

/**
 * Core 지점 목록 — 지점명순 표. 툴바의 보기 전환으로 운영 지점 표와 Core에서 삭제된 지점 표를 같은 자리에서 바꿔 본다
 * (`view=deleted`, 삭제 목록은 자체 cursor `deleted_cursor`). 서버는 지점명 정렬 방향과 cursor만 받으므로
 * 정렬·페이지 이동은 서버에 맡기고, 검색·신청 허용 필터는 불러온 한 페이지 안에서만 건다.
 * 조건은 모두 주소에 둬서 새로고침·링크 공유·뒤로가기와 상세에서 목록으로 돌아오기에 남는다.
 * 한 번에 한 보기만 보이므로 보이지 않는 보기의 조회는 쉬고, 보기를 오가도 다른 보기의 주소 조건은 지우지 않는다.
 */
export function NativePropertyListPage({ runtime }: { runtime: PropertyRuntime }) {
  const session = useOperatorSession(runtime.session);
  const location = useLocation();
  const navigate = useNavigate();
  const { params, set } = useUrlState();
  const view: NativePropertyListView = params.get('view') === 'deleted' ? 'deleted' : 'active';
  const deletedView = view === 'deleted';
  const cursor = params.get('cursor');
  const deletedCursor = params.get('deleted_cursor');
  const direction = params.get('sort_direction') === 'desc' ? 'desc' : 'asc';
  const query = parseNativePropertyListQuery(params);
  // "지점 등록" 버튼을 페이지 헤더 우측 액션 자리에 그리기 위한 슬롯. 헤더가 없으면(셸 밖) 폼 자리에 그대로 그린다.
  const [createSlot, setCreateSlot] = useState<HTMLElement | null>(null);

  const canRead =
    session.status === 'authorized' &&
    !session.deniedDomains.includes('property') &&
    !!session.authorization &&
    canReadProperties(session.authorization);
  const list = useNativePropertyList({
    runtime,
    generation: session.generation,
    cursor,
    direction,
    enabled: canRead && !deletedView,
  });
  const deleted = useDeletedNativeProperties({
    runtime,
    generation: session.generation,
    cursor: deletedCursor,
    enabled: canRead && deletedView,
  });

  // 상세로 들어갔다 돌아올 때 같은 페이지·정렬·검색 조건이 남도록 현재 운영 목록 주소를 싣는다(삭제된 지점 보기 상태는 뺀다).
  const activeListParams = new URLSearchParams(params);
  activeListParams.delete('deleted_cursor');
  activeListParams.delete('view');
  const activeListSearch = activeListParams.toString();
  const listReturnTo = nativePropertyListReturnTo(
    location.pathname + (activeListSearch ? `?${activeListSearch}` : ''),
  );
  const detailReturnQuery = listReturnTo
    ? `?${new URLSearchParams({ returnTo: listReturnTo }).toString()}`
    : '';
  const detailHref = (id: string) => `/properties/${id}${detailReturnQuery}`;

  // 읽기가 실패한 동안에는 이전에 받아 둔 목록도 화면 계산에 쓰지 않는다 — 실패 안내 옆에 옛 건수가 남으면 안 된다.
  const activePage = list.isError ? undefined : list.data;
  const deletedPage = deleted.isError ? undefined : deleted.data;
  const activeLoaded = activePage?.data ?? [];
  const deletedLoaded = deletedPage?.data ?? [];
  const activeRows = filterNativeProperties(activeLoaded, query);
  const deletedRows = filterDeletedProperties(deletedLoaded, query);

  // 지금 보이는 보기의 값. 두 보기가 같은 틀(헤더 건수·툴바 결과 수·표·페이지 바)을 쓴다.
  const current = deletedView
    ? {
        loaded: deletedLoaded.length,
        matched: deletedRows.length,
        // 삭제된 지점에는 신청 허용 값이 없으므로 걸 수 있는 조건은 검색어뿐이다.
        filtered: query.q !== '',
        hasPage: deletedPage !== undefined,
        cursor: deletedCursor,
        nextCursor: deletedPage?.next_cursor ?? null,
        isPending: deleted.isPending,
        isFetching: deleted.isFetching,
        isError: deleted.isError,
        error: deleted.error,
        refetch: () => void deleted.refetch(),
      }
    : {
        loaded: activeLoaded.length,
        matched: activeRows.length,
        filtered: isNativePropertyListFiltered(query),
        hasPage: activePage !== undefined,
        cursor,
        nextCursor: activePage?.next_cursor ?? null,
        isPending: list.isPending,
        isFetching: list.isFetching,
        isError: list.isError,
        error: list.error,
        refetch: () => void list.refetch(),
      };
  const paged = current.cursor !== null || current.nextCursor !== null;
  // 한 페이지에 다 담긴 경우에만 전체 건수를 안다 — 서버가 전체 건수를 주지 않는다.
  const total = current.hasPage && !paged ? current.loaded : null;
  // 표 위 줄에는 보기 설명과 페이지가 나뉜 경우의 범위 안내만 둔다 — 조건을 걸 때마다 줄이 생겼다 사라지면 표가 밀린다.
  const note = deletedView
    ? deletedPropertyListNote({ filtered: current.filtered, paged })
    : activePage
      ? nativePropertyListNote({
          loaded: current.loaded,
          filtered: current.filtered,
          paged,
          pageSize: NATIVE_PROPERTY_PAGE_SIZE,
        })
      : null;
  const empty = {
    ...nativePropertyListEmpty({
      view,
      filtered: current.filtered,
      paged,
      hasCursor: current.cursor !== null,
    }),
    icon: current.filtered ? SearchXIcon : deletedView ? Trash2Icon : Building2Icon,
  };
  const cursorKey = deletedView ? 'deleted_cursor' : 'cursor';
  const shownText = current.filtered
    ? `${String(current.matched)}/${String(current.loaded)}개 표시`
    : `${String(current.loaded)}개 표시`;

  return (
    <ListPage>
      <ShellHeader>
        <PageHeader
          title={
            <div className="flex items-center gap-2">
              <h1 className="title-md">지점</h1>
              {total !== null && (
                <Counter>{deletedView ? `삭제 ${String(total)}개` : `${String(total)}개`}</Counter>
              )}
            </div>
          }
          description="Core에 등록된 지점과 객실 정보를 확인합니다."
          actions={<div ref={setCreateSlot} className="flex items-center gap-2" />}
          className="items-start sm:items-center"
        />
      </ShellHeader>

      <NativePropertyCreate runtime={runtime} triggerSlot={createSlot} />

      <NativePropertyListToolbar
        view={view}
        // 보기를 바꾸면 그 보기의 첫 페이지부터 본다. 다른 보기의 주소 조건(페이지·정렬·신청 허용)은 남겨 돌아오면 그 자리다.
        onViewChange={(next) => {
          set(
            next === 'deleted'
              ? { view: 'deleted', deleted_cursor: null }
              : { view: null, deleted_cursor: null },
            { replace: false },
          );
        }}
        query={query}
        filtered={current.filtered}
        refreshing={current.isFetching && !current.isPending}
        onSearch={(value) => {
          set({ q: value || null });
        }}
        onApplication={(segment) => {
          set(patchFromApplicationSegment(segment));
        }}
        // 삭제된 지점 보기에서는 눈에 보이는 검색어만 지운다 — 보이지 않는 신청 허용 조건은 운영 지점으로 돌아갈 때를 위해 남긴다.
        onReset={() => {
          set(deletedView ? { q: null } : { q: null, application: null });
        }}
      />

      {note && <ListNote>{note}</ListNote>}

      <ListPage.Body
        role="region"
        aria-label={deletedView ? '삭제된 지점 목록' : '지점 목록'}
        tabIndex={0}
        className="focus-visible:outline-ring border-t focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-[-2px]"
      >
        {current.isPending && (
          <LoadingAnnouncement>
            {deletedView
              ? '삭제된 지점 목록을 불러오고 있습니다.'
              : '지점 목록을 불러오고 있습니다.'}
          </LoadingAnnouncement>
        )}
        <p className="sr-only">
          {deletedView
            ? '표가 화면보다 넓으면 좌우로 밀어 볼 수 있습니다.'
            : '표가 화면보다 넓으면 좌우로 밀어 볼 수 있습니다. Tab 키로 지점명 링크를 고르고 Enter 키로 엽니다.'}
        </p>
        {current.isError ? (
          <ListFailure view={view} error={current.error} onRetry={current.refetch} />
        ) : deletedView ? (
          <DeletedNativePropertyTable
            rows={deletedRows}
            isLoading={deleted.isPending}
            empty={empty}
          />
        ) : (
          <NativePropertyTable
            rows={activeRows}
            isLoading={list.isPending}
            direction={direction}
            empty={empty}
            detailHref={detailHref}
            // 서버가 지점명 순서만 정렬한다 — 방향만 바꾸고 해제는 기본(오름차순)으로 돌린다. 정렬이 바뀌면 처음 페이지로 간다.
            onSortChange={(sort) => {
              set(
                { sort_direction: sort === 'name,desc' ? 'desc' : null, cursor: null },
                { replace: false },
              );
            }}
            onOpen={(row) => {
              void navigate(detailHref(row.id), { state: nativePropertyNameHint(row) });
            }}
          />
        )}
      </ListPage.Body>

      <NativePropertyListPager
        label={deletedView ? '삭제된 지점 목록 페이지 이동' : '지점 목록 페이지 이동'}
        countText={current.hasPage ? shownText : ''}
        canFirst={current.cursor !== null}
        canNext={current.nextCursor !== null}
        busy={current.isFetching}
        onFirst={() => {
          set({ [cursorKey]: null }, { replace: false });
        }}
        onNext={() => {
          set({ [cursorKey]: current.nextCursor }, { replace: false });
        }}
      />
    </ListPage>
  );
}

/** 조회 실패 — 표 자리에 알림과 다시 시도. 삭제된 지점은 조회 기능이 서버에 아직 없을 수 있어(404) 그 경우를 따로 말한다. */
function ListFailure({
  view,
  error,
  onRetry,
}: {
  view: NativePropertyListView;
  error: unknown;
  onRetry: () => void;
}) {
  return (
    <div className="px-[var(--layout-gutter)] py-4">
      {view === 'deleted' && isApiError(error) && error.status === 404 ? (
        <p role="alert">
          삭제 지점 조회를 현재 서버에서 제공하지 않습니다. 조회 기능의 배포 상태를 확인해 주세요.
        </p>
      ) : (
        <LoadFailure
          error={error}
          subject={view === 'deleted' ? '삭제 지점 조회' : '지점 목록'}
          onRetry={onRetry}
        />
      )}
    </div>
  );
}
